import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Card components with exact Hyperkit styling
 *
 * Hyperkit Specs:
 * - Border Radius: 8px (rounded-lg)
 * - Padding: 24px (p-6)
 * - Border: 1px grey-200 (light), grey-800 (dark)
 * - Shadow: elevation-sm
 */

const cardVariants = cva(
  'rounded-lg border bg-white text-grey-900 dark:bg-grey-900 dark:text-grey-50',
  {
    variants: {
      variant: {
        default: 'border-grey-200 shadow-elevation-sm dark:border-grey-800',
        outline: 'border-grey-200 dark:border-grey-800',
        ghost: 'border-transparent bg-transparent shadow-none',
        elevated: 'border-grey-200 shadow-elevation-md dark:border-grey-800',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Card = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(cardVariants({ variant }), className)}
    {...props}
  />
));
Card.displayName = 'Card';

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 p-6', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  // eslint-disable-next-line jsx-a11y/heading-has-content
  <h3
    ref={ref}
    className={cn(
      'text-heading-md font-medium leading-none tracking-tight text-grey-900 dark:text-grey-50',
      className
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-body-sm text-grey-500 dark:text-grey-400', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-6 pt-0', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, cardVariants };
